/**
 * Arming switch and configuration for the visitor-events SDK (backend#411/#412).
 *
 * ## This ships INERT, and that is the point
 *
 * PR #244 landed the consent banner and the `visitorAnalyticsAllowed()` gate,
 * and stopped there because no sender existed. This module is the other half
 * of that stop: the sender now exists, but it does not run until someone sets
 * `VITE_VISITOR_ANALYTICS_ENDPOINT`.
 *
 * The reason is not caution for its own sake. **Legal review of the visitor
 * analytics text in `src/pages/PrivacyPage.tsx` is still outstanding** — #244's
 * own body records it as the founder's to arrange, on the web#68 precedent
 * (a legal page describing a data flow before anyone qualified read it). Code
 * that collects visitor data must be reviewable before it collects anything,
 * so the switch defaults off and the whole SDK is a no-op: no session id
 * minted, no listeners attached, no request made.
 *
 * This is the pattern the repo already uses for backends that exist but are
 * not armed — `CLIP_OG_IMAGE_ENDPOINT`, `TRENDING_AGGREGATES_URL`,
 * `WRAPPED_STATS_URL` (see CLAUDE.md, "Three of these read backend aggregates
 * that are not deployed yet"). Here the backend genuinely is live; what is
 * missing is the sign-off and the key.
 *
 * ## The three things that must all be present
 *
 * | field | env | why it cannot be defaulted |
 * | --- | --- | --- |
 * | `endpoint` | `VITE_VISITOR_ANALYTICS_ENDPOINT` | the arming switch. Absent = inert. |
 * | `writeKey` | `VITE_VISITOR_ANALYTICS_WRITE_KEY` | a `pk_` publishable write key. **Zero exist in production** as of this commit; minting is a separate owner surface. |
 * | `handle`   | `VITE_VISITOR_ANALYTICS_HANDLE`    | the key resolves to exactly one handle server-side, and every event carrying a different handle is rejected `handle_mismatch`. |
 *
 * ## Per-page injection beats env, because one env key cannot serve every creator
 *
 * A write key is scoped to ONE handle, and this site serves every creator's
 * clips from the same bundle. A single build-time key would therefore only
 * ever be able to report the build's own handle and would `handle_mismatch`
 * on every other clip page. So the real production shape is a per-page value
 * injected into the document by whichever server renderer already rewrites
 * the shell for that page (`api/clip-og.ts` and friends):
 *
 * ```html
 * <script>window.__MC_VISITOR_ANALYTICS__ = {"writeKey":"pk_...","handle":"someone"}</script>
 * ```
 *
 * That injection is NOT built here — it needs the minting UI to exist first.
 * The env triple is the shape that makes the SDK testable and lets a single
 * -handle deployment arm itself; the injected object is what production will
 * use, and it wins where both are present.
 *
 * **The two halves arm at different times, and it will catch someone out.**
 * `VITE_*` is substituted by Vite at BUILD time, so setting the Doppler var
 * does nothing to a deployment that is already built — it needs a redeploy.
 * The injected object is read at RUNTIME, on every admission, so a server
 * renderer can arm a single page with no rebuild at all. Verified against a
 * production build: with the env unset the env branch is constant-folded away
 * and `window.__MC_VISITOR_ANALYTICS__` is still read.
 *
 * ## What is deliberately not configurable
 *
 * `country` is not sent at all. The server derives it from the request IP and
 * silently ignores any client value (`api/routes/public_events.py`), so
 * sending one would be dead weight that merely looks like we collect geo
 * client-side.
 *
 * ## `variant` is CARRIED, never computed here — and that is not a shortcut
 *
 * #263 asks the SDK to assign A/B variants using the salted-hash scheme in the
 * backend's `services/ab_variants.py`. That function is one line of hashing:
 *
 * ```python
 * digest = hashlib.sha256(f"{_salt()}|{experiment_key}|{anchor}".encode()).digest()
 * return VARIANTS[digest[0] % len(VARIANTS)]
 * ```
 *
 * Porting the *arithmetic* is trivial. Sourcing its two inputs in a browser is
 * impossible, and each one is impossible for its own reason:
 *
 * 1. **The salt is a server secret.** `_salt()` walks
 *    `slug_salt` -> `secrets_encryption_key` -> `database_url` and returns the
 *    first that is set. `slug_salt` is not a field on `Settings` at all, so in
 *    practice production resolves to the Fernet secrets key or to the database
 *    URL — password included. Computing the same digest in page JS would mean
 *    shipping one of those to every visitor. The literal fallback
 *    (`"manticscore-ab-variants"`) is only reached when settings raise, so
 *    hard-coding it would agree with a broken deployment and nothing else.
 * 2. **The anchor is unreadable.** `assign_variant` anchors on `mc_anon`, and
 *    `routes/clips.py` sets that cookie `httponly=True`. `document.cookie`
 *    cannot see it, by design, and `credentials: "omit"` on our own request
 *    means it does not travel either (see the mc_anon note in `client.ts`).
 *
 * So a client-side implementation could not be *checked* against the server —
 * it would be a coin flip that merely looks like the server's coin flip, and
 * an assignment that silently disagrees corrupts both arms of every
 * experiment. Nothing is ported. The field is carried instead: whichever
 * server renderer can see both the salt and the cookie computes the
 * assignment and injects it, and the SDK stamps it on every event.
 *
 * **Injected only, never from env.** A `VITE_` variant is fixed at build time,
 * so it would put 100% of traffic in one arm and report it as a split — worse
 * than no experiment, which is the exact failure `ab_variants.py` refuses when
 * it returns `None` rather than inventing an anchor. Per-page injection is the
 * only shape where the value can differ per visitor.
 *
 * Until something injects it, `variant` is `undefined`, every event carries no
 * variant, and `GET /v1/variants` keeps reporting `assignment.wired = false`
 * and `unassigned_visitors`. That is the honest reading of the current state.
 */

import { normalizeEnvValue } from "@/lib/env";

/**
 * The A/B arm this page view belongs to.
 *
 * Closed enum, equal to `VARIANTS` in the backend's `services/ab_variants.py`
 * and to migration 152's `variant` CHECK list. Two arms, not n — the event
 * envelope carries one `variant` per event and the significance test is a
 * two-proportion z-test, both of which are two-armed by construction.
 */
export type VisitorVariant = "A" | "B";

const VARIANTS: readonly VisitorVariant[] = ["A", "B"];

/** Everything the sender needs. The first three fields are required. */
export type VisitorAnalyticsConfig = {
  /** Absolute URL of `POST /api/public/events`. */
  endpoint: string;
  /** Publishable write key, `pk_` prefixed. */
  writeKey: string;
  /** The handle the write key resolves to server-side. */
  handle: string;
  /**
   * The server's A/B assignment for this page view, when it made one.
   *
   * Absent in every deployment as of this commit — nothing injects it yet.
   * See the header for why this is carried rather than computed, and why it
   * is deliberately not readable from env.
   */
  variant?: VisitorVariant;
};

/** The page-injected override. Set by a server renderer, never by the client. */
type InjectedConfig = Partial<VisitorAnalyticsConfig>;

declare global {
  interface Window {
    __MC_VISITOR_ANALYTICS__?: InjectedConfig;
  }
}

/**
 * A publishable key is safe in a page; a secret key is not. Requiring the
 * prefix means a mis-pasted `sk_` credential is never transmitted to a public
 * ingest endpoint — the SDK simply stays inert instead.
 */
const PUBLISHABLE_PREFIX = "pk_";

function fromWindow(): InjectedConfig {
  try {
    return window.__MC_VISITOR_ANALYTICS__ ?? {};
  } catch {
    return {};
  }
}

/**
 * The resolved config, or `null` when the SDK must stay inert.
 *
 * Read on every admission so that arming, disarming and a page-injected key
 * arriving late all take effect without a reload. Cheap: three string reads.
 * Deliberately not memoised into a module const — that would freeze
 * `import.meta.env` at import time and make the switch untestable.
 */
export function resolveVisitorAnalyticsConfig(): VisitorAnalyticsConfig | null {
  const injected = fromWindow();

  const endpoint =
    normalizeEnvValue(injected.endpoint) ||
    normalizeEnvValue(import.meta.env.VITE_VISITOR_ANALYTICS_ENDPOINT);
  if (!endpoint) return null;

  // Degrade silently with no key. Another agent owns the minting UI, and a
  // missing key is the expected state until it ships — console noise here
  // would be a permanent warning on every clip page for a known gap.
  const writeKey =
    normalizeEnvValue(injected.writeKey) ||
    normalizeEnvValue(import.meta.env.VITE_VISITOR_ANALYTICS_WRITE_KEY);
  if (!writeKey || !writeKey.startsWith(PUBLISHABLE_PREFIX)) return null;

  const handle =
    normalizeEnvValue(injected.handle) ||
    normalizeEnvValue(import.meta.env.VITE_VISITOR_ANALYTICS_HANDLE);
  if (!handle) return null;

  return { endpoint, writeKey, handle, variant: injectedVariant(injected.variant) };
}

/**
 * The injected variant, or undefined for anything that is not a live arm.
 *
 * Validated against the closed enum rather than passed through: the server
 * rejects an out-of-enum `variant` for the whole event, so a typo'd injection
 * would turn every event on the page into a `422` instead of an event with no
 * variant. Dropping it here degrades to "unassigned", which is a state
 * `GET /v1/variants` already reports honestly.
 */
function injectedVariant(value: unknown): VisitorVariant | undefined {
  return VARIANTS.find((variant) => variant === value);
}

/**
 * Cheap "is there any point loading the sender?" test.
 *
 * Kept separate from the sender module so the eager mount can answer it
 * without pulling the SDK into the entry bundle: with the switch unset the
 * sender's chunk is never even requested.
 */
export function visitorAnalyticsArmed(): boolean {
  return resolveVisitorAnalyticsConfig() !== null;
}
